import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import {
  QuestionnaireSectionDetailProps,
  QuestionnaireSectionProps,
  QuestionProps,
} from '@/types/questionnaire';
import { QuestionColumns } from '../../Questions/components/QuestionColumns';
import { CreateQuestionModal } from '../../Questions/components/CreateQuestionModal';
import { EditQuestionModal } from '../../Questions/components/EditQuestionModal';
import { DeleteQuestionDialog } from '../../Questions/components/DeleteQuestionDialog';
import { EditSectionModal } from './EditSectionModal';
import { useModal } from '@/contexts/ModalContext';
import { router } from '@inertiajs/react';
import { DeleteSectionDialog } from './DeleteSectionDialog';

export function SectionDetails({
  section,
  isLoading,
  templateSections,
}: {
  section?: QuestionnaireSectionDetailProps;
  isLoading: boolean;
  templateSections: QuestionnaireSectionProps[];
}) {
  const { openModal, closeModal } = useModal();
  const handleDeleteSuccess = (deletedSection: QuestionnaireSectionProps) => {
    const fallbackRoute = `/admin/questionnaire_templates/${section?.questionnaire_template_id}`;

    if (!templateSections || templateSections.length <= 1) {
      router.get(fallbackRoute);
      return;
    }

    const currentIndex = templateSections.findIndex(
      (s) => s.id === deletedSection.id
    );
    const nextSection =
      templateSections[currentIndex + 1] || templateSections[currentIndex - 1];

    if (nextSection) {
      router.get(
        `/admin/questionnaire_templates/${section?.questionnaire_template_id}/questionnaire_sections?section_id=${nextSection.id}`
      );
    } else {
      router.get(fallbackRoute);
    }
  };

  if (isLoading) return <div>Loading...</div>;
  if (!section) return 'Select a section to view details';
  return (
    <>
      <div className="flex flex-col gap-4 w-full">
        <div className="flex justify-between items-start">
          <h1 className="text-xl font-bold">{section.section_name}</h1>
          <div className="flex gap-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const modalId = openModal({
                  component: (
                    <EditSectionModal
                      section={section}
                      allSections={templateSections}
                      onClose={() => closeModal(modalId)}
                    />
                  ),
                  heading: 'Edit Section',
                });
              }}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                const modalId = openModal({
                  component: (
                    <DeleteSectionDialog
                      section={section}
                      onClose={() => closeModal(modalId)}
                      onSuccess={() => handleDeleteSuccess(section)}
                    />
                  ),
                  heading: 'Delete Section',
                });
              }}
            >
              Delete
            </Button>
          </div>
        </div>
        <p className="text-muted-foreground text-sm">{section.description}</p>
        <div className="mt-6 flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            Questions ({section.questions.length})
          </h3>
          <Button
            size={'sm'}
            onClick={() => {
              const modalId = openModal({
                component: (
                  <CreateQuestionModal
                    sectionId={section.id}
                    onClose={() => closeModal(modalId)}
                  />
                ),
                heading: 'Create Question',
              });
            }}
          >
            Create Question
          </Button>
        </div>
        <DataTable<QuestionProps>
          data={section.questions}
          columns={QuestionColumns({
            sectionId: section.id,
            onEdit: (question) => {
              const modalId = openModal({
                component: (
                  <EditQuestionModal
                    question={question}
                    allQuestions={section.questions}
                    onClose={() => closeModal(modalId)}
                  />
                ),
                heading: 'Edit Question',
              });
            },
            onDelete: (question) => {
              const modalId = openModal({
                component: (
                  <DeleteQuestionDialog
                    question={question}
                    onClose={() => closeModal(modalId)}
                  />
                ),
                heading: 'Delete Question',
              });
            },
          })}
        />
      </div>
    </>
  );
}
